import { type ReactElement } from 'react';
import { useTable, List, ShowButton } from '@refinedev/antd';
import type { HttpError, CrudFilters } from '@refinedev/core';
import {
  Table,
  Tag,
  Space,
  Input,
  Form,
  Select,
  List as AntList,
  Card,
  Grid,
  Tooltip,
  Typography,
} from 'antd';

import type { AdminConsentLog } from '@/types';

const { useBreakpoint } = Grid;
const { Text } = Typography;

// ---------------------------------------------------------------------------
// Search form shape
// ---------------------------------------------------------------------------

interface ConsentLogSearchForm {
  consentType?: string;
  consentGiven?: boolean;
  orderId?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const CONSENT_TYPE_COLORS: Record<string, string> = {
  tos: 'blue',
  privacy_policy: 'purple',
  biometric: 'orange',
  marketing: 'green',
};

function consentTypeTag(value: string): ReactElement {
  const color = CONSENT_TYPE_COLORS[value] ?? 'default';
  return <Tag color={color}>{value}</Tag>;
}

function truncateId(id: string | null): string {
  if (!id) return '—';
  return id.length > 8 ? `${id.slice(0, 8)}...` : id;
}

// ---------------------------------------------------------------------------
// Mobile card component
// ---------------------------------------------------------------------------

interface MobileConsentLogCardProps {
  record: AdminConsentLog;
}

function MobileConsentLogCard({
  record,
}: MobileConsentLogCardProps): ReactElement {
  return (
    <Card size="small" style={{ width: '100%' }}>
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <div>
          <strong>Type:</strong> {consentTypeTag(record.consentType)}
        </div>
        <div>
          <strong>Given:</strong>{' '}
          {record.consentGiven ? (
            <Tag color="green">✓ Given</Tag>
          ) : (
            <Tag color="red">✗ Declined</Tag>
          )}
        </div>
        <div>
          <strong>Order:</strong>{' '}
          {record.orderId ? (
            <a href={`/orders/show/${record.orderId}`}>
              {truncateId(record.orderId)}
            </a>
          ) : (
            '—'
          )}
        </div>
        <div>
          <strong>Created:</strong>{' '}
          {new Date(record.createdAt).toLocaleDateString()}
        </div>
        <Space size="small">
          <ShowButton hideText size="small" recordItemId={record.id} />
        </Space>
      </Space>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ConsentLogList(): ReactElement {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const { tableProps, searchFormProps } = useTable<
    AdminConsentLog,
    HttpError,
    ConsentLogSearchForm
  >({
    resource: 'consent-logs',
    syncWithLocation: true,
    sorters: {
      initial: [{ field: 'createdAt', order: 'desc' }],
    },
    onSearch: (values) => {
      const filters: CrudFilters = [
        {
          field: 'consentType',
          operator: 'eq',
          value: values.consentType,
        },
        {
          field: 'consentGiven',
          operator: 'eq',
          value:
            values.consentGiven !== undefined
              ? String(values.consentGiven)
              : undefined,
        },
        {
          field: 'orderId',
          operator: 'eq',
          value: values.orderId,
        },
      ];
      return filters;
    },
  });

  return (
    <List>
      <Space style={{ marginBottom: 16, flexWrap: 'wrap' }}>
        <Form {...searchFormProps} layout="inline">
          <Form.Item name="consentType">
            <Select
              placeholder="Filter by type"
              allowClear
              style={{ width: 180 }}
              options={[
                { label: 'ToS', value: 'tos' },
                { label: 'Privacy Policy', value: 'privacy_policy' },
                { label: 'Biometric', value: 'biometric' },
                { label: 'Marketing', value: 'marketing' },
              ]}
              onChange={(val: string | undefined) => {
                searchFormProps.form?.setFieldsValue({ consentType: val });
                searchFormProps.form?.submit();
              }}
            />
          </Form.Item>
          <Form.Item name="consentGiven">
            <Select
              placeholder="Filter by given"
              allowClear
              style={{ width: 160 }}
              options={[
                { label: 'Given', value: true },
                { label: 'Declined', value: false },
              ]}
              onChange={(val: boolean | undefined) => {
                searchFormProps.form?.setFieldsValue({ consentGiven: val });
                searchFormProps.form?.submit();
              }}
            />
          </Form.Item>
          <Form.Item name="orderId">
            <Input.Search
              placeholder="Search by Order ID"
              allowClear
              onSearch={(value) => {
                searchFormProps.form?.setFieldsValue({ orderId: value });
                searchFormProps.form?.submit();
              }}
              style={{ width: 240 }}
            />
          </Form.Item>
        </Form>
      </Space>

      {isMobile ? (
        <AntList
          dataSource={tableProps.dataSource ? [...tableProps.dataSource] : []}
          pagination={
            tableProps.pagination && typeof tableProps.pagination === 'object'
              ? {
                  current: tableProps.pagination.current,
                  pageSize: tableProps.pagination.pageSize,
                  total: tableProps.pagination.total,
                  onChange: tableProps.pagination.onChange,
                }
              : false
          }
          renderItem={(record) => (
            <AntList.Item key={record.id} style={{ padding: '8px 0' }}>
              <MobileConsentLogCard record={record} />
            </AntList.Item>
          )}
        />
      ) : null}

      {!isMobile ? (
        <Table {...tableProps} rowKey="id" scroll={{ x: 1100 }} size="small">
          <Table.Column<AdminConsentLog>
            dataIndex="customerId"
            title="Customer"
            render={(value: string | null) =>
              value ? (
                <a href={`/customers/show/${value}`}>{value.slice(0, 8)}</a>
              ) : (
                '—'
              )
            }
            width={120}
          />

          <Table.Column<AdminConsentLog>
            dataIndex="orderId"
            title="Order ID"
            render={(value: string | null) =>
              value ? (
                <Tooltip title={value}>
                  <a href={`/orders/show/${value}`}>
                    <Text copyable={{ text: value }}>
                      {value.slice(0, 8)}...
                    </Text>
                  </a>
                </Tooltip>
              ) : (
                '—'
              )
            }
            width={160}
          />

          <Table.Column<AdminConsentLog>
            dataIndex="consentType"
            title="Type"
            render={(value: string) => consentTypeTag(value)}
            width={150}
          />

          <Table.Column<AdminConsentLog>
            dataIndex="consentVersion"
            title="Version"
            width={100}
          />

          <Table.Column<AdminConsentLog>
            dataIndex="consentGiven"
            title="Given"
            render={(value: boolean) =>
              value ? (
                <Tag color="green">✓ Yes</Tag>
              ) : (
                <Tag color="red">✗ No</Tag>
              )
            }
            width={100}
            align="center"
          />

          <Table.Column<AdminConsentLog>
            dataIndex="ipAddress"
            title="IP Address"
            render={(value: string | null) => value ?? '—'}
            width={140}
          />

          <Table.Column<AdminConsentLog>
            dataIndex="createdAt"
            title="Created"
            render={(value: string) => new Date(value).toLocaleDateString()}
            width={120}
          />

          <Table.Column<AdminConsentLog>
            title="Actions"
            fixed="right"
            width={80}
            render={(_: unknown, record: AdminConsentLog) => (
              <Space size="small">
                <ShowButton hideText size="small" recordItemId={record.id} />
              </Space>
            )}
          />
        </Table>
      ) : null}
    </List>
  );
}
